import React, { useMemo } from 'react';
import Page from 'components/page/Page';
import { UserClaimsList } from 'components/users/UserClaimsList';
import { Button, LoadingSpinner } from 'common.ui';
import { devToolsClient, internalUsersClient, usersClient } from 'api/AdgangClients';
import { ResponseError } from 'api/adgang';
import { useParams } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import { Row, Col } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { store } from 'react-notifications-component';

interface IParams {
  id: string;
}

function InternalUserClaims() {
  const { id } = useParams<IParams>();
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['internalUser', id],
    queryFn: () =>
      internalUsersClient.apiInternalUsersInternalUserIdGet({
        internalUserId: id
      })
  });

  const { data: claims } = useQuery({
    queryKey: ['userClaims', user?.userId],
    queryFn: () =>
      user?.userId
        ? usersClient.apiUsersUserIdAccessClaimsGet({
            userId: user.userId
          })
        : null,
    enabled: !!user?.userId
  });

  const refreshCacheMutation = useMutation({
    mutationFn: (derivedUserId: string) =>
      devToolsClient.apiDevToolsRefreshUserCacheDerivedUserIdPost({
        derivedUserId
      }),
    onSuccess: () => {
      store.addNotification({
        title: 'Success',
        message: 'User cache refreshed successfully!',
        type: 'success',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 5000
        }
      });
      // Invalidate queries to refetch fresh data
      queryClient.resetQueries({ queryKey: ['userClaims', user?.userId] });
      queryClient.resetQueries({ queryKey: ['internalUser', id] });
    },
    onError: (error) => {
      const errorMessage =
        error instanceof ResponseError
          ? `Failed to refresh user cache. Status: ${error.response.status}`
          : 'Failed to refresh user cache. An unexpected error occurred.';
      store.addNotification({
        title: 'Error',
        message: errorMessage,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 10000
        }
      });
    }
  });

  const fullName = useMemo(() => {
    if (user) {
      return `${user.givenName} ${user.surname}`;
    }
    return '';
  }, [user]);

  return (
    <Page header='Intern ansatt Claims' title={['Intern ansatt', fullName, 'Claims']}>
      {user && claims ? (
        <>
          <Container fluid>
            <Row>
              <Col lg='12'>
                <Button
                  text={refreshCacheMutation.isPending ? 'Refreshing...' : 'Refresh UserCache for this user'}
                  disabled={refreshCacheMutation.isPending}
                  onClick={() => {
                    if (user?.internalUserId) {
                      refreshCacheMutation.mutate(user.internalUserId);
                    }
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col lg='12'>
                <UserClaimsList claims={claims} userId={user.userId ?? ''} />
              </Col>
            </Row>
          </Container>
        </>
      ) : (
        <LoadingSpinner />
      )}
    </Page>
  );
}

export default InternalUserClaims;
